import { Component, HostListener, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { WalletService } from '../../core/services/wallet.service';

@Component({
  selector: 'app-keygen',
  standalone: true,
  template: `
    <!-- SVG filter to add subtle displacement (tube wobble) -->
    <svg width="0" height="0" style="position:absolute">
      <defs>
        <filter id="crtDistort">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.002 0.8"
            numOctaves="1"
            seed="3"
            result="noise"
          >
            <animate
              attributeName="baseFrequency"
              dur="8s"
              values="0.002 0.8;0.004 0.7;0.003 0.9;0.002 0.8"
              repeatCount="indefinite"
            />
          </feTurbulence>
          <feDisplacementMap
            in="SourceGraphic"
            in2="noise"
            scale="2"
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </defs>
    </svg>

    <div class="crt">
      <div class="keygen" role="application" aria-label="Digital Insanity Keygen (Mock)">
        <div class="kg-header">
          <div class="brand" aria-hidden="true">DIGITAL<br />INSANITY</div>
          <div class="sub">v1.9 · Protection: <b>Base32</b> + <b>Custom</b> + <b>ECDSA-113</b></div>
        </div>

        <div class="kg-body">
          <div class="row">
            <div class="label">Product Name:</div>
            <button class="slot select" (click)="toggleOptions($event)" #selectBox>
              <div class="select-toggle">
                <div class="value mono">{{ currentProduct }}</div>
                <div class="caret" aria-hidden="true"></div>
              </div>
              <div
                class="options"
                [class.open]="optionsOpen"
                role="listbox"
                aria-label="Product options"
              >
                @for (p of products; track p) {
                <button
                  class="option"
                  [class.active]="p === currentProduct"
                  (click)="choose(p); $event.stopPropagation()"
                >
                  {{ p }}
                </button>
                }
              </div>
            </button>
          </div>

          <div class="row">
            <div class="label">Machine ID:</div>
            <div class="slot"><input [(ngModel)]="machineId" /></div>
          </div>

          <div class="row">
            <div class="label">Serial Number:</div>
            <div class="slot"><input disabled [(ngModel)]="serialNumber" /></div>
          </div>

          <div class="row">
            <div class="label">Activation/Authentication Code:</div>
            <div class="slot"><input disabled [(ngModel)]="activationCode" /></div>
          </div>

          <div class="buttons">
            <button class="btn" (click)="patch()">Patch ...</button>
            <button class="btn primary" (click)="generate()">Generate</button>
            <button class="btn" (click)="about()">About</button>
            <button class="btn" (click)="exit()">Exit</button>
          </div>
          <div class="hint">{{ hint }}</div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        color: #dfe7ef;
        font-family: Segoe UI, Tahoma, Geneva, Verdana, sans-serif;
      }

      /* === CRT wrapper for wobble/scanlines === */
      .crt {
        position: relative;
        height: 100%;
        padding: 10px;
        border-radius: 10px;
        background: #020305;
        box-shadow: 0 30px 80px rgba(0, 0, 0, 0.65), inset 0 0 50px rgba(0, 180, 255, 0.06);
        animation: wobble 8s ease-in-out infinite alternate;
        filter: url(#crtDistort);
        display: grid;
        place-items: center;
      }
      .crt::before {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        border-radius: inherit;
        background: repeating-linear-gradient(
          180deg,
          rgba(255, 255, 255, 0.04) 0 1px,
          rgba(0, 0, 0, 0) 2px 3px
        );
        mix-blend-mode: overlay;
        opacity: 0.35;
        animation: scan 9s linear infinite;
      }
      .crt::after {
        content: '';
        position: absolute;
        inset: -2px;
        pointer-events: none;
        border-radius: inherit;
        background: radial-gradient(
          120% 90% at 50% 50%,
          rgba(0, 0, 0, 0) 60%,
          rgba(0, 0, 0, 0.55) 100%
        );
        mix-blend-mode: multiply;
        animation: flicker 2.4s steps(2, end) infinite;
        opacity: 0.9;
      }
      @keyframes wobble {
        0% {
          transform: perspective(800px) rotateX(0.15deg) rotateZ(0.02deg) skewX(0.15deg)
            translateX(0);
        }
        50% {
          transform: perspective(800px) rotateX(0.2deg) rotateZ(-0.02deg) skewX(-0.12deg)
            translateX(-0.8px);
        }
        100% {
          transform: perspective(800px) rotateX(0.1deg) rotateZ(0.04deg) skewX(0.12deg)
            translateX(0.8px);
        }
      }
      @keyframes scan {
        0% {
          background-position-y: 0;
        }
        100% {
          background-position-y: 120px;
        }
      }
      @keyframes flicker {
        0% {
          opacity: 0.92;
        }
        50% {
          opacity: 0.86;
        }
        100% {
          opacity: 0.92;
        }
      }

      .keygen {
        width: min(720px, 100%);
        max-height: 100%;
        display: flex;
        flex-direction: column;
        background: linear-gradient(#0b0f14, #0b0f14) padding-box,
          linear-gradient(180deg, #3f556b, #10161d) border-box;
        border: 1px solid transparent;
        border-radius: 6px;
        overflow: hidden;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6), inset 0 0 0 1px rgba(255, 255, 255, 0.03);
      }

      .kg-header {
        position: relative;
        height: 180px;
        display: grid;
        place-items: center;
        background: repeating-linear-gradient(
            0deg,
            rgba(255, 255, 255, 0.08) 0 1px,
            transparent 1px 2px
          ),
          radial-gradient(1200px 300px at 50% -60%, #2b2f36, #1a1e25 45%, #12161d 80%);
        filter: contrast(1.05) saturate(1.02);
      }
      .kg-header .brand {
        text-transform: uppercase;
        letter-spacing: 0.06em;
        line-height: 0.82;
        text-align: center;
        user-select: none;
        font-weight: 800;
        font-size: 64px;
        color: #cfd7df;
        text-shadow: 0 3px 0 rgba(0, 0, 0, 0.6), 0 0 18px rgba(0, 0, 0, 0.35),
          -1px 0 0 rgba(0, 180, 255, 0.22), 1px 0 0 rgba(255, 60, 0, 0.18);
      }
      .kg-header .sub {
        position: absolute;
        bottom: 10px;
        left: 10px;
        font-size: 12px;
        color: #cfe9ff;
        opacity: 0.8;
        background: #0b1219;
        border: 1px solid #294355;
        border-radius: 3px;
        padding: 3px 6px;
        box-shadow: inset 0 0 8px rgba(87, 170, 220, 0.15);
      }
      .kg-header .sub b {
        color: #ffffff;
      }

      /* Mobile adjustments */
      @media (max-width: 768px), (pointer: coarse) {
        .kg-header {
          height: 140px;
        }
        .kg-header .brand {
          font-size: 44px;
        }
        .kg-header .sub {
          font-size: 11px;
          bottom: 6px;
          left: 8px;
        }
      }

      .kg-body {
        padding: 14px;
        background: linear-gradient(180deg, #0d131a, #0b1016);
        overflow: auto;
        flex: 1;
        min-height: 0; /* needed for overflow in flex */
      }
      .row {
        display: grid;
        grid-template-columns: 180px 1fr;
        gap: 10px;
        align-items: center;
        margin: 10px 0;
      }
      .label {
        color: #9bb0c2;
        font-size: 13px;
        text-shadow: 0 1px 0 rgba(0, 0, 0, 0.7);
      }

      /* Select and options adjustments */
      .slot.select {
        position: relative;
      }
      .select-toggle {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
      }
      .options {
        position: absolute;
        left: 0;
        right: 0;
        top: calc(100% + 6px);
        max-height: 220px;
        overflow: auto;
        background: #0b1219;
        border: 1px solid #294355;
        border-radius: 0;
        box-shadow: 0 12px 28px rgba(0, 0, 0, 0.4);
        display: none;
        z-index: 20;
      }
      .options.open {
        display: block;
      }
      .option {
        width: 100%;
        text-align: left;
        padding: 8px 10px;
      }
      .option.active {
        background: rgba(41, 67, 85, 0.35);
      }

      /* Remove rounded corners from buttons and inputs in this component */
      .keygen button,
      .keygen input,
      .keygen select {
        border-radius: 0;
        padding: 5px;
        margin-right: 10px;
        margin-bottom: 5px;
      }
    `,
  ],
  imports: [FormsModule],
})
export class KeygenComponent {
  products = [
    'Cypher Fly v0.0.1',
    'DVD Architect Studio 4.5 Series',
    'Vegas Pro 8.1 (64 bit) Series',
    'Vegas Pro 9.0 (32 and 64 bit) Series',
    'Vegas Movie Studio 8.0 Series',
    'Vegas Movie Studio 9.0 Series',
    'Vegas Movie Studio Platinum 8.0 Series',
  ];

  currentProduct = this.products[0];
  optionsOpen = false;

  machineId = '@myMachine';
  serialNumber = '1R8-5H5B-HQV4-CXHM';
  activationCode = 'C8M9JN1HY-9ZR8KMH50-EC7CPNTF8-7EKKQDDR';
  hint = 'Music: "Unreal Super Hero 3" by Kenet & Rez';

  private alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  private walletService = inject(WalletService);

  toggleOptions(ev: Event) {
    const target = ev.target as HTMLElement;
    // only toggle when clicking the top area, not options container
    if (target.closest('.options')) return;
    ev.stopPropagation();
    this.optionsOpen = !this.optionsOpen;
  }
  choose(p: string) {
    this.currentProduct = p;
    this.optionsOpen = false;
    this.pulse(`Selected: ${p}`);
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.optionsOpen) this.optionsOpen = false;
  }

  generate() {
    this.serialNumber = `${this.g(3)}-${this.g(4)}-${this.g(4)}-${this.g(4)}`;
    this.activationCode = `${this.g(9)}-${this.g(9)}-${this.g(9)}-${this.g(9)}`;
    this.pulse('Generated values.');
  }
  patch() {
    this.hint = 'Patching...';
    setTimeout(() => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      if (this.machineId.includes(this.walletService.walletConnection().actor)) {
        this.hint = 'Patching... done ✅';
      } else {
        this.hint = 'Patching... error ❌';
      }
    }, 3000);
  }
  about() {
    alert('Digital Insanity\nv1.9 * Protection: Base32 + Custom + ECDSA-113\n.');
  }
  exit() {
    this.pulse('Exiting... just kidding, I live in your DOM.');
  }

  private g(len: number) {
    let s = '';
    for (let i = 0; i < len; i++)
      s += this.alphabet[Math.floor(Math.random() * this.alphabet.length)];
    return s;
  }
  private pulse(t: string) {
    this.hint = t;
    setTimeout(() => (this.hint = 'Music: "Unreal Super Hero 3" by Kenet & Rez'), 5000);
  }
}
